/**
 * YouTube 트렌드 API 서비스
 */
import { getToken } from '../shared/auth/tokenManager';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.ilouli.com';

/**
 * API 요청 헬퍼
 */
async function apiRequest(endpoint, options = {}) {
  const token = getToken();

  const response = await fetch(`${API_URL}${endpoint}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers
    },
    ...options
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'API 요청 실패');
  }

  return data;
}

/**
 * 트렌드 요약 조회
 */
export async function getTrends(period = 'daily', category = null) {
  const params = new URLSearchParams({ period });
  if (category) params.append('category', category);

  return apiRequest(`/api/youtube/trends?${params}`);
}

/**
 * 감성 분석 조회
 */
export async function getSentiment(keyword, period = 7) {
  const params = new URLSearchParams({ keyword, period: period.toString() });
  return apiRequest(`/api/youtube/sentiment?${params}`);
}

/**
 * 콘텐츠 추천 조회
 */
export async function getRecommendations(category = null) {
  const params = category ? `?category=${category}` : '';
  return apiRequest(`/api/youtube/recommendations${params}`);
}

/**
 * 수집된 영상 목록 조회
 */
export async function getVideos(options = {}) {
  const params = new URLSearchParams();
  if (options.source_type) params.append('source_type', options.source_type);
  if (options.keyword) params.append('keyword', options.keyword);
  if (options.limit) params.append('limit', options.limit.toString());
  if (options.offset) params.append('offset', options.offset.toString());
  if (options.sort) params.append('sort', options.sort);
  if (options.order) params.append('order', options.order);

  return apiRequest(`/api/youtube/videos?${params}`);
}

/**
 * 특정 영상 상세 조회
 */
export async function getVideoDetail(videoId) {
  return apiRequest(`/api/youtube/videos/${videoId}`);
}

/**
 * 키워드 통계 조회
 */
export async function getKeywords(days = 7) {
  return apiRequest(`/api/youtube/keywords?days=${days}`);
}

/**
 * 키워드 추가
 */
export async function addKeyword(keyword, category = null) {
  return apiRequest('/api/youtube/keywords', {
    method: 'POST',
    body: JSON.stringify({ keyword, category })
  });
}

/**
 * 채널 목록 조회
 */
export async function getChannels() {
  return apiRequest('/api/youtube/channels');
}

/**
 * 채널 추가
 */
export async function addChannel(channelId, category = null) {
  return apiRequest('/api/youtube/channels', {
    method: 'POST',
    body: JSON.stringify({ channelId, category })
  });
}

/**
 * 수집 상태 조회
 */
export async function getStatus() {
  return apiRequest('/api/youtube/status');
}

/**
 * 리포트 목록 조회
 */
export async function getReports(type = null, limit = 10) {
  const params = new URLSearchParams({ limit: limit.toString() });
  if (type) params.append('type', type);

  return apiRequest(`/api/youtube/reports?${params}`);
}

/**
 * 수동 수집 트리거 (관리자)
 */
export async function triggerCollection(type = 'full') {
  return apiRequest('/api/youtube/collect', {
    method: 'POST',
    body: JSON.stringify({ type })
  });
}

/**
 * 수동 분석 트리거 (관리자)
 */
export async function triggerAnalysis(type = 'daily', category = null, timeSlot = null) {
  return apiRequest('/api/youtube/analyze', {
    method: 'POST',
    body: JSON.stringify({ type, category, timeSlot })
  });
}

// ==========================================
// 6시간 주기 트렌드 API (신규)
// ==========================================

/**
 * 사용 가능한 시간대 목록 조회
 */
export async function getTimeSlots() {
  return apiRequest('/api/youtube/time-slots');
}

/**
 * 시간대별 트렌딩 영상 조회
 */
export async function getTrending(options = {}) {
  const params = new URLSearchParams();
  if (options.timeSlot) params.append('timeSlot', options.timeSlot);
  if (options.date) params.append('date', options.date);
  if (options.limit) params.append('limit', options.limit.toString());

  return apiRequest(`/api/youtube/trending?${params}`);
}

/**
 * HOT 점수 상위 영상 조회
 */
export async function getHotVideos(days = 1, limit = 20) {
  const params = new URLSearchParams({ days: days.toString(), limit: limit.toString() });
  return apiRequest(`/api/youtube/hot-videos?${params}`);
}

/**
 * 시간대별 키워드 통계 조회
 */
export async function getKeywordsBySlot(options = {}) {
  const params = new URLSearchParams();
  if (options.timeSlot) params.append('timeSlot', options.timeSlot);
  if (options.date) params.append('date', options.date);

  return apiRequest(`/api/youtube/keywords/by-slot?${params}`);
}

/**
 * 시간대별 트렌드 요약 조회
 */
export async function getSlotTrend(options = {}) {
  const params = new URLSearchParams();
  if (options.timeSlot) params.append('timeSlot', options.timeSlot);
  if (options.date) params.append('date', options.date);

  return apiRequest(`/api/youtube/slot-trend?${params}`);
}

export default {
  getTrends,
  getSentiment,
  getRecommendations,
  getVideos,
  getVideoDetail,
  getKeywords,
  addKeyword,
  getChannels,
  addChannel,
  getStatus,
  getReports,
  triggerCollection,
  triggerAnalysis,
  // 6시간 주기 API
  getTimeSlots,
  getTrending,
  getHotVideos,
  getKeywordsBySlot,
  getSlotTrend
};
